// src/auth/login-page.tsx
// The login surface, composed from Signal DS atoms (TextField + Button + Banner).
// Now the entry point into the full login-recovery suite: a "Forgot password?"
// link to /forgot-password, an account-lockout countdown on repeated failure
// (Flutter login_repository_impl 400 → CheckLockOutTime), and a success flash
// shown after a reset/create-password round-trip. No hand-rolled inputs/buttons;
// layout + colour are DS tokens only.
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, TextField, Banner } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from './auth-store';
import { AuthBrandHeading, AuthLinkButton } from './auth-screen-shell';
import { AccountLockoutBanner } from './account-lockout-banner';
import { checkLockOutTime } from './recovery-service';
import { ProfileUnavailableError, TokenGrantError } from './token-service';

interface LoginLocationState {
  /** Success message handed over by the recovery flow after a reset/create. */
  flash?: string;
}

export function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();
  const flash = ((location.state ?? {}) as LoginLocationState).flash ?? null;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Remaining lockout minutes from CheckLockOutTime; > 0 ⇒ account locked and
  // the countdown banner is shown + submit is disabled.
  const [lockoutMinutes, setLockoutMinutes] = useState(0);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // recoverActiveSession: a manual web sign-in may collide with an active
      // session for the same user elsewhere (e.g. the Flutter app on the same
      // backend), which makes GetUserDataToken 409. Without recovery the 409 is
      // swallowed and we fall back to JWT-only data — no isOnboarded (→ the
      // onboarding gate fires) and no practitionerId/practiceId (→ an empty
      // practice selector). Recover with the SkipConflict header (Flutter parity,
      // same as the bringup auto-login) so the full profile loads. (PROD-1298)
      await login(username, password, { recoverActiveSession: true });
    } catch (e) {
      // Flutter parity (login_repository_impl.dart:69-77): the password grant's
      // failure status decides what a failed sign-in means — a 400 and a 401 are
      // NOT the same failure and must not share a code path.
      //   - 400 ⇒ bad credentials, but possibly a lockout. Probe
      //     CheckLockOutTime; a returned `minute` ⇒ the account is locked → show
      //     the countdown. Otherwise ⇒ the generic invalid-credentials message.
      //   - 401 ⇒ the account itself is archived/inactive (not a bad-password
      //     case at all). Flutter never probes lockout here — it goes straight to
      //     the contact-support message. Probing lockout on a 401 would report
      //     "Invalid email/password" for an account that isn't a credentials
      //     problem in the first place.
      // The credentials were fine but GetUserDataToken did not hand back a profile,
      // so the sign-in was refused rather than completed with no role (see
      // ProfileUnavailableError). Saying "invalid email/password" here would send the
      // user to reset a password that works; say what actually happened and leave them
      // on this form to try again.
      if (e instanceof ProfileUnavailableError) {
        setError('We could not load your account details. Please sign in again — if it keeps happening, contact Scrips support.');
        return;
      }
      const status = e instanceof TokenGrantError ? e.status : null;
      if (status === 401) {
        setError('Your account is archived or inactive. Please contact Scrips support.');
      } else if (status === 400) {
        const lock = await checkLockOutTime(username);
        if (lock.minute != null && lock.minute > 0) {
          setLockoutMinutes(lock.minute);
          setError(null);
        } else {
          setError('Invalid email address or incorrect password.');
        }
      } else {
        // Any other/unrecognized failure (network error, non-token-grant
        // rejection, etc.) — fall back to the generic message rather than
        // guessing at lockout state.
        setError('Invalid email address or incorrect password.');
      }
    } finally {
      setBusy(false);
    }
  }

  const locked = lockoutMinutes > 0;

  return (
    <main className="grid min-h-app-screen place-items-center bg-surface-background font-sans">
      <form
        onSubmit={onSubmit}
        className="flex w-90 flex-col gap-4 rounded-lg border border-border-default bg-surface-card p-8"
      >
        <AuthBrandHeading heading="Sign in" />
        {flash && <Banner variant="success" role="status">{flash}</Banner>}
        {locked && (
          <AccountLockoutBanner minutes={lockoutMinutes} onExpire={() => setLockoutMinutes(0)} />
        )}
        <TextField
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          disabled={locked}
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          error={error ?? undefined}
          disabled={locked}
        />
        <Button type="submit" variant="primary" loading={busy} disabled={busy || locked}>
          Sign in
        </Button>
        <div className="flex justify-center gap-4">
          <AuthLinkButton onClick={() => navigate('/forgot-password')}>
            Forgot password?
          </AuthLinkButton>
          {/* Entry point for a newly-invited provider who lands on sign-in
              without knowing /create-password exists — the invite email links
              to the site root with email+code, but nothing on this screen
              previously pointed at the create-password flow. Same
              AuthLinkButton styling as "Forgot password?" — no new component. */}
          <AuthLinkButton onClick={() => navigate('/create-password')}>
            Sign up with access code
          </AuthLinkButton>
        </div>
      </form>
    </main>
  );
}
